import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { notificationsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Bell, ArrowLeft, Loader2, Heart, Shield, CheckCheck, Eye } from 'lucide-react';
import { toast, Toaster } from 'sonner';

const NOTIF_ICONS = {
  extol: Heart,
  admin: Shield,
  reaction: Heart
};

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadNotifications = async () => {
      try {
        const { data } = await notificationsApi.getAll();
        setNotifications(data);
      } catch (err) { console.error('Failed to load notifications:', err); toast.error('Failed to load notifications'); }
      finally { setLoading(false); }
    };
    loadNotifications();
  }, []);

  const markAllRead = async () => {
    try {
      await notificationsApi.markRead();
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      toast.success('All marked as read');
    } catch { toast.error('Failed to mark as read'); }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Toaster position="top-right" richColors />
      <header className="sticky top-0 z-50 bg-[#FDFBF7]/90 backdrop-blur-xl border-b border-[#1a1a2e]/5">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-[#1a1a2e]/50 hover:text-[#1a1a2e]" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4" /><span className="text-sm">Dashboard</span>
          </Link>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" onClick={markAllRead} className="text-[#7c3aed] rounded-full" data-testid="mark-all-read">
              <CheckCheck className="w-4 h-4 mr-1.5" />Mark all read
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-[#1a1a2e] mb-6 flex items-center gap-2" data-testid="notifications-heading">
          <Bell className="w-6 h-6 text-[#7c3aed]" />Notifications
          {unreadCount > 0 && <span className="text-sm bg-[#db2777] text-white px-2 py-0.5 rounded-full">{unreadCount}</span>}
        </h1>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-[#7c3aed] animate-spin" /></div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-16" data-testid="no-notifications">
            <Bell className="w-12 h-12 text-[#1a1a2e]/10 mx-auto mb-3" />
            <p className="text-[#1a1a2e]/40">No notifications yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map(n => {
              const Icon = NOTIF_ICONS[n.type] || Bell;
              return (
                <Card key={n.id} className={`border-[#1a1a2e]/5 ${n.read ? 'bg-white' : 'bg-purple-50/50 border-[#7c3aed]/10'} shadow-sm`} data-testid={`notif-${n.id}`}>
                  <CardContent className="p-4 flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${n.read ? 'bg-[#1a1a2e]/5' : 'bg-[#7c3aed]/10'}`}>
                      <Icon className={`w-4 h-4 ${n.read ? 'text-[#1a1a2e]/40' : 'text-[#7c3aed]'}`} />
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm ${n.read ? 'text-[#1a1a2e]/60' : 'text-[#1a1a2e] font-medium'}`}>{n.message}</p>
                      <p className="text-xs text-[#1a1a2e]/30 mt-1">{new Date(n.created_at).toLocaleDateString()}</p>
                    </div>
                    {n.profile_id && (
                      <Link to={`/tribute/${n.profile_id}`}>
                        <Button variant="ghost" size="icon" className="shrink-0 rounded-full"><Eye className="w-4 h-4 text-[#1a1a2e]/40" /></Button>
                      </Link>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
